import {
  AfterViewInit,
  Component,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { MapConfig } from 'src/app/_shared/config/map.config';
import { PropertyService as WorkspacePropertyService } from 'src/app/workspace/service/property.service';
import { MapState } from 'src/app/workspace/state/map.state';
import { PropertyState } from 'src/app/workspace/state/property.state';
import constants from 'src/constant.json';
import { SearchBarFacade } from '../search-bar.facade';

declare const google: any;

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
})
export class SearchComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() addressType: string;
  @ViewChild('searchField') searchField: any;

  constant: any = constants;
  autoCompleteInput = '';
  queryWait: boolean;
  subpremise: string;

  onDestroyNotifier$ = new Subject<void>();

  private lastHandledSearch = '';
  private destroyed = false;
  private searchRequestId = 0;

  private autocomplete: any = null;
  private autocompleteInitializing = false;
  private onPlaceSelect: ((event: any) => void) | null = null;
  private onInputChange: (() => void) | null = null;

  constructor(
    private searchBarFacade: SearchBarFacade,
    private workspacePropertyService: WorkspacePropertyService,
    private mapConfig: MapConfig,
    private mapState: MapState,
    private propertyState: PropertyState,
    private route: ActivatedRoute,
    private ngZone: NgZone
  ) { }

  ngOnInit(): void {
    this.searchBarFacade
      .getFullAddress()
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((address) => {
        if (address && address !== this.autoCompleteInput) {
          this.autoCompleteInput = address;
          // CHANGED: ngModel no longer drives the visible text, so push it
          // into the new element explicitly.
          this.setInputValue(address);
        }
      });
  }

  ngAfterViewInit(): void {
    this.getPlaceAutocomplete();

    // Defer the initial URL search until the current view check finishes.
    Promise.resolve().then(() => {
      if (this.destroyed) {
        return;
      }

      this.route.queryParamMap
        .pipe(
          map((params) => (params.get('search') || '').trim()),
          distinctUntilChanged(),
          takeUntil(this.onDestroyNotifier$)
        )
        .subscribe((search) => {
          if (!search) {
            this.lastHandledSearch = '';

            // Ignore any pending result for the previous URL.
            this.searchRequestId++;
            return;
          }

          if (search === this.lastHandledSearch) {
            return;
          }

          this.lastHandledSearch = search;
          this.autoCompleteInput = search;
          this.setInputValue(search);
          this.invokeEvent();
        });
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.searchRequestId++;

    if (this.autocomplete) {
      if (this.onInputChange) {
        this.autocomplete.removeEventListener('input', this.onInputChange);
      }

      if (this.onPlaceSelect) {
        this.autocomplete.removeEventListener('gmp-select', this.onPlaceSelect);
      }

      this.autocomplete.remove();
      this.autocomplete = null;
    }

    this.onInputChange = null;
    this.onPlaceSelect = null;

    this.onDestroyNotifier$.next();
    this.onDestroyNotifier$.complete();
  }

  async getPlaceAutocomplete(): Promise<void> {
    if (
      this.destroyed ||
      this.autocomplete ||
      this.autocompleteInitializing ||
      !this.searchField ||
      !this.searchField.nativeElement
    ) {
      return;
    }

    this.autocompleteInitializing = true;

    try {
      const placesLibrary = await google.maps.importLibrary('places');
      if (this.destroyed) {
        return;
      }

      const inputElement = this.searchField.nativeElement;
      const parent = inputElement.parentElement;

      if (!parent) {
        return;
      }

      const PlaceAutocompleteElement = (placesLibrary as any).PlaceAutocompleteElement;

      const element = new PlaceAutocompleteElement({
        componentRestrictions: { country: 'US' },
        placeholder: 'Search address, ZIP, neighborhood, school district, MLS Number (prefix with #)',
      });

      parent.replaceChild(element, inputElement);
      this.autocomplete = element;

      if (this.autoCompleteInput) {
        element.value = this.autoCompleteInput;
      }


      this.onInputChange = () => {
        this.ngZone.run(() => {
          this.autoCompleteInput = element.value || '';
        });
      };

      this.onPlaceSelect = async (event: any) => {
        try {
          const place = event.placePrediction.toPlace();
          await place.fetchFields({ fields: ['formattedAddress'] });

          this.ngZone.run(() => {
            this.invokeEvent(place.formattedAddress);
          });
        } catch (error) {
          console.warn('Place selection failed:', error);
        }
      };

      element.addEventListener('input', this.onInputChange);
      element.addEventListener('gmp-select', this.onPlaceSelect);
    } finally {
      this.autocompleteInitializing = false;
    }
  }


  invokeEvent(formattedAddress?: string): void {
    if (this.destroyed) {
      return;
    }

    const search = (formattedAddress || this.autoCompleteInput || '').trim();

    if (!search) {
      return;
    }

    this.autoCompleteInput = search;

    if (search.startsWith('#')) {
      // Invalidate any pending address search.
      this.searchRequestId++;

      const mlsNumber = search.substring(1).trim();

      if (mlsNumber) {
        this.workspacePropertyService.fetchListingsByMlsNumber(mlsNumber);
      }

      return;
    }

    // Works for URL searches, the Go button, and autocomplete selections.
    this.getPlaceFromGeocoder();
  }

  getPlaceFromGeocoder(): void {
    const address = (this.autoCompleteInput || '').trim();

    if (!address || this.destroyed) {
      return;
    }

    const requestId = ++this.searchRequestId;

    new google.maps.Geocoder().geocode(
      { address },
      (results, status) => {
        this.ngZone.run(() => {
          // A newer search takes precedence over this result.
          if (
            this.destroyed ||
            requestId !== this.searchRequestId
          ) {
            return;
          }

          if (
            status !== google.maps.GeocoderStatus.OK ||
            !results ||
            !results.length
          ) {
            if (this.lastHandledSearch === address) {
              this.lastHandledSearch = '';
            }

            console.warn('Address search failed:', status);
            return;
          }

          const resolvedPlace = results[0];

          let routeName = '';
          let subPremise = '';
          let streetNumber = '';
          let city = '';
          let state = '';
          let zip = '';

          (resolvedPlace.address_components || []).forEach((component) => {
            const types = component.types;

            if (types.includes('route')) {
              routeName = component.short_name;
            }

            if (types.includes('subpremise')) {
              subPremise = component.short_name;
            }

            if (types.includes('street_number')) {
              streetNumber = component.short_name;
            }

            if (types.includes('locality')) {
              city = component.short_name;
            } else if (!city && types.includes('sublocality')) {
              city = component.short_name;
            }

            if (types.includes('administrative_area_level_1')) {
              state = component.short_name;
            }

            if (types.includes('postal_code')) {
              zip = component.short_name;
            }
          });

          const formattedAddress = (
            resolvedPlace.formatted_address || address
          ).trim();

          this.lastHandledSearch = formattedAddress;
          this.autoCompleteInput = formattedAddress;
          this.setInputValue(formattedAddress);

          if (streetNumber && routeName) {
            const addressObj: any = {
              StreetAddress: `${streetNumber} ${routeName}`,
              City: city,
              State: state,
              PostalCode: zip,
              logInput: true,
            };

            if (subPremise) {
              const normalizedUnit = subPremise.toLowerCase();
              const unitPrefixes: string[] =
                this.constant.UNIT_PREFIX || [];

              const hasUnitPrefix = unitPrefixes.some((prefix) =>
                normalizedUnit.includes(prefix.toLowerCase())
              );

              addressObj.StreetAddress += hasUnitPrefix
                ? ` ${subPremise}`
                : ` # ${subPremise}`;
            }

            this.propertyState.propertyDetailsSheetInput = addressObj;
            this.mapState.mapZoomValue = this.mapConfig.streetLevelZoom;
          } else {
            this.mapState.mapZoomValue = this.mapConfig.defaultZoom;
          }

          this.searchBarFacade.setAddressObj(resolvedPlace);
        });
      }
    );
  }

  loadPropertyDetailsForListing(listing: any): void {
    if (!listing || !listing.StdAddress) {
      return;
    }

    const addressObj: any = {
      StreetAddress: listing.StdAddress.UnparsedAddress,
      City: listing.StdAddress.City,
      State: listing.StdAddress.StateOrProvince,
      PostalCode: listing.StdAddress.PostalCode,
      logInput: true,
    };

    this.propertyState.propertyDetailsSheetInput = addressObj;
    this.mapState.mapZoomValue = this.mapConfig.streetLevelZoom;
  }


  private setInputValue(value: string): void {
    if (this.autocomplete && this.autocomplete.value !== value) {
      this.autocomplete.value = value;
    }
  }
}